/**
 * Tenant self-service "Configuration & Sauvegardes" page.
 *
 * Two areas under a `Tabs` control:
 *  - **Historique** — the configurable `ConfigHistoryCard` (change history +
 *    revert).
 *  - **Sauvegardes** — the configurable `ConfigBackupsCard` (snapshot list,
 *    restore, create backup).
 *
 * Both cards are the `withComponentRules`-wrapped exports, so admin-authored
 * layout rules apply per tenant. The page wraps them in a `RuleContextProvider`
 * exposing `tenant_slug` (the only useful conditional target here).
 */

import { useMemo } from 'react';

import { useParams } from 'react-router-dom';

import { Text } from '@components/base/display';
import { Tabs } from '@components/base/navigation';
import { VStack } from '@components/base/layout';
import { RuleContextProvider } from '@components/shared/configurable';

import { buildConfigRuleContext } from '../contextVariables';
import {
  ConfigBackupsCardConfigurable as ConfigBackupsCard,
  ConfigHistoryCardConfigurable as ConfigHistoryCard,
} from '../components/register';

export function ConfigVersioningPage() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  const ruleContextValue = useMemo(
    () => buildConfigRuleContext({ tenantSlug }),
    [tenantSlug],
  );

  return (
    <RuleContextProvider value={ruleContextValue}>
      <div className="max-w-6xl mx-auto p-6">
        <VStack gap={6}>
          <div>
            <Text as="h1" size="2xl" weight="bold">
              Configuration &amp; Sauvegardes
            </Text>
            <Text size="sm" color="muted">
              Consultez l’historique des modifications de configuration,
              restaurez une version, et gérez les sauvegardes du locataire.
            </Text>
          </div>

          <Tabs defaultValue="history">
            <Tabs.List>
              <Tabs.Tab value="history">Historique</Tabs.Tab>
              <Tabs.Tab value="backups">Sauvegardes</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="history">
              <div className="pt-4">
                <ConfigHistoryCard />
              </div>
            </Tabs.Panel>
            <Tabs.Panel value="backups">
              <div className="pt-4">
                <ConfigBackupsCard />
              </div>
            </Tabs.Panel>
          </Tabs>
        </VStack>
      </div>
    </RuleContextProvider>
  );
}
